import React from 'react'

import { Box, Collapse, Stack } from '@mui/material'

import { Card, FIELD_HEIGHT, FieldLabel, SectionNav, Skeleton } from '@repo/ui'
import { Search, StickyFormHeader, TocSpacer } from 'components'
import { vhMinus } from 'utils/textZoom'
import { FORM_SECTION_SX } from 'utils/formSectionSx'

import { OWNER_FORM_SECTIONS } from '../CreateOwnerPage/ownerFormShared'

const COLUMN_WIDTH = 720

/**
 * Loading placeholder for the Edit Owner form (§19).
 *
 * ## What it replaces
 *
 * `{isLoading ? <Loader /> : …}` — a centred spinner in place of a 1366px form: the 200px TOC rail,
 * a 167px sticky header with a search field, and three cards.
 *
 * `CreateOwnerPage` needs no equivalent: its only `isLoading` is the CREATE MUTATION's, not a fetch,
 * so it has no loading branch to replace.
 *
 * ## Measured, not guessed
 *
 * Off `/owners/:id/edit` (viewport 1711, form column x=624 w=720):
 *
 *   outer row       y=24  h=1366; rail 200 at x=400, spacer 200 at x=1368
 *   sticky header   y=24  h=167
 *   Details card    y=215 h=519   content 417
 *   Address card    y=758 h=401   content 299
 *   Marketing card  y=1183 h=207  content 105
 *
 * Card = `50 + 28 (title) + 24 + content`, so only content is reproduced. Within it:
 *
 *   Details    photo 122 → 24 → name 73 → 24 → email/phone 73 → 24 → birthday/gender/country 77
 *   Address    address 99 → 24 → city/state 75 → 24 → postal/country 77
 *   Marketing  consent row 40 → 24 → consent row 40
 *
 * ## One input height (VRG-703)
 *
 * Every control on this form is FIELD_HEIGHT (49). Until VRG-703 a `TextField` here was pinned to
 * 47 (`textFieldHeightSx`, a workaround for `SelectField`'s old hardcoded 47) while the date and
 * country pickers rendered 49, so text rows were 2px shorter than select rows; the measurements
 * above predate that and the text rows are now 75 / 75 / 101 instead of 73 / 73 / 99. A row with
 * an inline "(Optional)" helper is still 2px taller again, because its label row is 20 not 18.
 *
 * ## Construction
 *
 * As with the product form, nearly all of this page is static: the real `SectionNav` fed the same
 * `OWNER_FORM_SECTIONS` the two owner pages use, the real `StickyFormHeader` + `Search`, the real
 * `Card` per section, and the real `FieldLabel` with the real label text — so every label height and
 * label→control gap is right by construction. The `Collapse` + `FORM_SECTION_SX` wrapper is also
 * the real one, which matters: its animation's `from` state leaves a live `translateY(6px)` on the
 * settled page, so a wrapper-less card sits 6px off.
 *
 * The literals left are the control heights (47 / 49 / the 96px avatar / the 42x22 switch) and the
 * helper line boxes — the parts a loading state genuinely cannot know.
 */
export const OwnerEditPageSkeleton = ({ onBack }: { onBack: () => void }) => {
  /** An inline "(Optional)" beside a label: 20px on the WRAPPER, which is what lifts the label row
   *  from 18 to 20 (and so the field from 73/75 to 75/77). A `lineHeight` here does nothing — the
   *  bar inside is a block, so the box would collapse to the bar's own height. */
  const optionalHelper = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 20 }}>
      <Skeleton width={53} height={14} />
    </Box>
  )

  /**
   * One labelled field. `height` is the CONTROL's: FIELD_HEIGHT (49) for every control on
   * this form since VRG-703 (it was 47 for a TextField and a select, 49 for the rest).
   */
  const Field = ({
    label,
    height = FIELD_HEIGHT,
    optional = false,
  }: {
    label: string
    height?: number
    optional?: boolean
  }) => (
    <Box>
      <FieldLabel label={label} adornment={optional ? optionalHelper : undefined} />
      <Skeleton variant="rounded" height={height} />
    </Box>
  )

  /** A half-width column in a two-up row, matching the real `1 1 calc(50% - 12px)`. */
  const Half = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 200 }}>{children}</Box>
  )

  /** A third-width column, matching the real `1 1 calc(33.333% - 16px)`. */
  const Third = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 200 }}>{children}</Box>
  )

  const Row = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>{children}</Box>
  )

  const Section = ({ id, children }: { id: string; children: React.ReactNode }) => (
    <Collapse in timeout={250} unmountOnExit>
      <Box id={id} sx={FORM_SECTION_SX}>
        {children}
      </Box>
    </Collapse>
  )

  /** One marketing consent row: the switch, then a title + description block. 40px tall. */
  const ConsentRow = ({ subWidth }: { subWidth: number }) => (
    <Stack direction="row" alignItems="flex-start" spacing={3}>
      <Skeleton variant="rounded" width={42} height={22} sx={{ borderRadius: '999px' }} />
      <Box>
        <Box sx={{ height: 18, display: 'flex', alignItems: 'center' }}>
          <Skeleton width={160} height={14} />
        </Box>
        <Box sx={{ height: 22, display: 'flex', alignItems: 'flex-end' }}>
          <Skeleton width={subWidth} height={13} />
        </Box>
      </Box>
    </Stack>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 6,
        width: '100%',
        mb: 6,
        justifyContent: 'center',
        alignItems: { xs: 'center', md: 'stretch' },
      }}
    >
      {/* The real rail on the real section list. `onChange` is a no-op (nothing to scroll to yet);
          Back is the page's own cancel, so the one control a user might reach for still works. */}
      <SectionNav
        items={OWNER_FORM_SECTIONS.map((s) => ({ label: s.label, value: s.id }))}
        value={OWNER_FORM_SECTIONS[0].id}
        onChange={() => {}}
        onBack={onBack}
      />

      <Box
        sx={{
          flex: 1,
          maxWidth: COLUMN_WIDTH,
          width: { xs: '100%', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minHeight: vhMinus(160),
        }}
      >
        <StickyFormHeader
          title="Edit owner"
          actions={
            <>
              {/* Bars, not real Buttons: Save would submit an empty form, and Cancel is already
                  reachable from the rail's Back. 40px is the header-action height. */}
              <Skeleton variant="rounded" width={78} height={40} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rounded" width={71} height={40} sx={{ borderRadius: 2 }} />
            </>
          }
        >
          <Search onChange={() => {}} fullWidth={true} placeholder="Search fields..." />
        </StickyFormHeader>

        {/* DETAILS */}
        <Section id="details">
          <Card title="Details">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Profile photo: label + the 96px avatar row (avatar, then Upload + its file-type
                  line). 122 = 18 label + 8 gap + 96. */}
              <Box>
                <FieldLabel label="Profile Photo" />
                <Stack direction="row" spacing={3} alignItems="center">
                  <Skeleton variant="circular" width={96} height={96} />
                  <Stack spacing={2}>
                    <Skeleton variant="rounded" width={80} height={32} />
                    <Skeleton width={116} height={14} />
                  </Stack>
                </Stack>
              </Box>
              <Field label="Full Name" />
              <Row>
                <Half>
                  <Field label="Email" />
                </Half>
                <Half>
                  <Field label="Phone" />
                </Half>
              </Row>
              <Row>
                {/* All three carry an inline "(Optional)" and a 49px control, so this row is 77. */}
                <Third>
                  <Field label="Birthday" height={FIELD_HEIGHT} optional />
                </Third>
                <Third>
                  <Field label="Gender" height={FIELD_HEIGHT} optional />
                </Third>
                <Third>
                  <Field label="Country" height={FIELD_HEIGHT} optional />
                </Third>
              </Row>
            </Box>
          </Card>
        </Section>

        {/* ADDRESS */}
        <Section id="address">
          <Card title="Address">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* The autocomplete, plus its own helper line beneath: 20 + 8 + 49 + 4 + 18 = 99. */}
              <Box>
                <FieldLabel label="Address" adornment={optionalHelper} />
                <Skeleton variant="rounded" height={FIELD_HEIGHT} />
                <Box sx={{ mt: 1, height: 18, display: 'flex', alignItems: 'center' }}>
                  <Skeleton width={280} height={14} />
                </Box>
              </Box>
              <Row>
                <Half>
                  <Field label="City" optional />
                </Half>
                <Half>
                  <Field label="State / Province" optional />
                </Half>
              </Row>
              <Row>
                <Half>
                  <Field label="Postal code" optional />
                </Half>
                <Half>
                  {/* The country picker is 49, not 47 — which is what makes this row 77. */}
                  <Field label="Country" height={FIELD_HEIGHT} optional />
                </Half>
              </Row>
            </Box>
          </Card>
        </Section>

        {/* MARKETING */}
        <Section id="marketing">
          <Card title="Marketing">
            <Stack spacing={6}>
              <ConsentRow subWidth={275} />
              <ConsentRow subWidth={322} />
            </Stack>
          </Card>
        </Section>
      </Box>

      <TocSpacer />
    </Box>
  )
}
